/**
 * /well-servicing/tickets — the Well Servicing field ticket register
 * (GET /well-servicing/tickets, fieldiq-api#1294, WS-4).
 *
 * A ticket is drafted from its job's tour sheets (job → Tickets → Create
 * ticket), so this register has no New button; it says where tickets come
 * from. The status and customer filters live in the URL. Totals are the API's: a draft has
 * no snapshot total yet, so it reads `—` rather than a figure nobody approved,
 * and a ticket with an unpriced line carries the "Unpriced" flag (the API's
 * `unpricedCount`) — never a $0. Nothing on this page adds money up.
 */
import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, FileCheck2, Send } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useWsCustomers, useWsMeta } from '../../../hooks/useWellServicing';
import { useWsTickets } from '../../../hooks/useWsTickets';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wsScopeText, wsTicketStatusSkin, WS_TICKET_STATUS_SKIN } from '../../../lib/ws-tickets';
import type { WsTicket, WsTicketCatalog } from '../../../types/ws-tickets';
import { useOpenRecord } from '../../downhole/recordPage';
import { WsUnpricedFlag } from './TicketBits';
import { WS_EYEBROW, WS_INPUT, WsBadge, WsChip, WsChipRow, WsEmpty, WsPanel, WsRegister, type WsColumn } from '../WsBits';

/** The figure a register row shows: the office snapshot once signed, the priced-lines sum never (a list row has no lines). */
export const wsTicketTotalText = (t: WsTicket) => fmtCurrency(t.totalAmount, t.currency);

export default function WsTicketsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const customer = searchParams.get('customer') ?? '';
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  const setStatus = (next: string) => setParam('status', next);
  const catalog = (useWsMeta().data?.data ?? null) as WsTicketCatalog | null;
  const customers = useWsCustomers().data ?? [];
  const listQuery = useWsTickets({ status: status || undefined, customer_id: customer || undefined });
  const allQuery = useWsTickets();
  const tickets = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (t: WsTicket) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const statuses = catalog?.ticketStatuses?.length
    ? catalog.ticketStatuses
    : Object.entries(WS_TICKET_STATUS_SKIN).map(([value, s]) => ({ value, label: s.label }));

  const columns: WsColumn<WsTicket>[] = [
    { key: 'number', header: 'Ticket', render: (t) => <span className="font-mono font-extrabold text-white">{t.ticketNumber || '—'}</span> },
    {
      key: 'job',
      header: 'Job',
      render: (t) => (
        <>
          <p className="font-mono">{t.jobNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {t.wellName || '—'}
            {t.rigNumber ? ` · ${t.rigNumber}` : ''}
          </p>
        </>
      ),
    },
    { key: 'customer', header: 'Customer', render: (t) => t.customerName || '—' },
    { key: 'scope', header: 'Covers', render: (t) => wsScopeText(t) },
    { key: 'status', header: 'Status', render: (t) => <WsBadge skin={wsTicketStatusSkin(t.status)} value={t.status} /> },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (t) => (
        <span className="whitespace-nowrap">
          <span className="font-mono" data-testid="ws-ticket-total">{wsTicketTotalText(t)}</span>
          <WsUnpricedFlag count={t.unpricedCount} />
        </span>
      ),
    },
  ];

  return (
    <div data-testid="ws-tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title="Field Tickets"
        description="Tickets drafted from the office-approved tour sheets, approved by the office and signed by the company man."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Drafts', tone: 'neutral', loading: allQuery.isLoading, value: countOf((t) => t.status === 'draft') },
          { icon: <Send className="size-4" aria-hidden="true" />, label: 'With the customer', tone: 'accent', loading: allQuery.isLoading, value: countOf((t) => t.status === 'office_approved') },
          { icon: <FileCheck2 className="size-4" aria-hidden="true" />, label: 'Customer approved', tone: 'success', loading: allQuery.isLoading, value: countOf((t) => t.status === 'customer_approved') },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            A ticket is drafted from its job: open the <Link className="underline" to="/well-servicing/jobs">job</Link>, then Tickets → Create ticket.
          </p>
          <WsChipRow label="Filter by status">
            <WsChip label="All" active={!status} onClick={() => setStatus('')} count={allQuery.isLoading ? undefined : countOf(() => true)} />
            {statuses.map((s) => (
              <WsChip key={s.value} label={s.label} active={status === s.value} onClick={() => setStatus(s.value)} count={allQuery.isLoading ? undefined : countOf((t) => t.status === s.value)} />
            ))}
          </WsChipRow>
          <div className="grid gap-3 sm:grid-cols-[16rem]">
            <select className={WS_INPUT} aria-label="Filter by customer" value={customer} onChange={(e) => setParam('customer', e.target.value)}>
              <option value="">Every customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <WsPanel eyebrow="Ticket register" title="Field tickets" testId="ws-ticket-register" footer={!listQuery.isLoading && tickets.length ? `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}` : undefined}>
            <WsRegister
              query={listQuery}
              rows={tickets}
              columns={columns}
              rail={(t) => wsTicketStatusSkin(t.status).rail}
              onOpen={(t) => openRecord(`/well-servicing/tickets/${t.id}`)}
              subject="field tickets"
              testId="ws-ticket"
              minWidth="min-w-[860px]"
              empty={status || customer ? <WsEmpty>No ticket matches these filters.</WsEmpty> : <WsEmpty>No field tickets yet. Draft one from a job.</WsEmpty>}
              card={(t) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{t.ticketNumber || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{t.jobNumber || '—'} · {t.wellName || '—'} · {t.customerName || '—'}</p>
                    </div>
                    <WsBadge skin={wsTicketStatusSkin(t.status)} value={t.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {wsScopeText(t)} · Total {wsTicketTotalText(t)}
                    <WsUnpricedFlag count={t.unpricedCount} testId="ws-unpriced-flag-card" />
                  </p>
                </>
              )}
            />
          </WsPanel>
        </div>
      </section>
    </div>
  );
}
